import React, { useEffect, useRef, useState } from 'react'

export default function WebcamModal({ open, onClose, onCapture }) {
    const videoRef = useRef(null)
    const canvasRef = useRef(null)
    const [stream, setStream] = useState(null)
    const [useFront, setUseFront] = useState(true)
    const [preview, setPreview] = useState(null)
    const [status, setStatus] = useState('')
    const [isStarting, setIsStarting] = useState(false)

    useEffect(() => {
        if (!open) {
            stopStream()
            setPreview(null)
            setStatus('')
            return
        }

        startStream()

        return () => {
            stopStream()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, useFront])

    function stopStream() {
        if (stream) {
            stream.getTracks().forEach(t => t.stop())
        }
        setStream(null)
    }

    async function startStream() {
        try {
            setIsStarting(true)
            setStatus('Abrindo câmera...')
            stopStream()

            const constraints = {
                video: {
                    facingMode: useFront ? 'user' : 'environment'
                },
                audio: false
            }

            const s = await navigator.mediaDevices.getUserMedia(constraints)
            setStream(s)

            if (videoRef.current) {
                videoRef.current.srcObject = s
                const v = videoRef.current
                v.onloadedmetadata = () => {
                    v.play().catch(() => {})
                }
            }

            setStatus(useFront ? 'Usando câmera frontal' : 'Usando câmera traseira')
        } catch (e) {
            console.error(e)
            if (e.name === 'NotAllowedError') {
                setStatus('Permissão da câmera negada. Verifique as permissões do navegador.')
            } else if (e.name === 'NotFoundError') {
                setStatus('Nenhuma câmera foi encontrada neste dispositivo.')
            } else {
                setStatus('Erro ao acessar a câmera: ' + e.message)
            }
        } finally {
            setIsStarting(false)
        }
    }

    function handleClose() {
        stopStream()
        setPreview(null)
        setStatus('')
        onClose?.()
    }

    function doCapture() {
        const video = videoRef.current
        const canvas = canvasRef.current
        if (!video || !canvas) return
        if (!video.videoWidth || !video.videoHeight) {
            setStatus('Aguardando vídeo carregar...')
            return
        }

        canvas.width = video.videoWidth
        canvas.height = video.videoHeight
        const ctx = canvas.getContext('2d')
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
        const data = canvas.toDataURL('image/png')
        setPreview(data)

        // Opcional: libera a câmera após capturar (bom para mobile)
        stopStream()
        setStatus('Prévia da foto. Se não gostou, é só tirar outra 😉')
    }

    function usePhoto() {
        if (preview) {
            onCapture?.(preview)
            handleClose()
        }
    }

    function handleRetake() {
        setPreview(null)
        startStream()
    }

    function toggleCamera() {
        setUseFront(v => !v)
    }

    if (!open) return null

    return (
        <div
            className="modal-backdrop"
            style={{
                zIndex: 999999,
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.75)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '16px'
            }}
            role="dialog"
            aria-modal="true"
        >
            <div
                className="modal-card"
                style={{
                    width: '100%',
                    maxWidth: 420,
                    background: '#020617',
                    borderRadius: 16,
                    padding: 16,
                    boxShadow: '0 20px 45px rgba(0,0,0,0.6)',
                    border: '1px solid rgba(148,163,184,0.2)'
                }}
            >
                {/* Header */}
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 8
                    }}
                >
                    <div>
                        <h5
                            className="text-white"
                            style={{ margin: 0, fontSize: 16, fontWeight: 600 }}
                        >
                            {preview ? 'Prévia da foto' : 'Tirar foto'}
                        </h5>
                        <p
                            style={{
                                margin: 0,
                                marginTop: 2,
                                fontSize: 12,
                                color: '#9ca3af'
                            }}
                        >
                            {preview
                                ? 'Confira se está nítida antes de enviar.'
                                : 'Centralize bem o rosto/documento e toque em Capturar.'}
                        </p>
                    </div>

                    <button
                        className="icon-btn"
                        onClick={handleClose}
                        title="Fechar"
                        style={{
                            border: 'none',
                            background: 'transparent',
                            color: '#e5e7eb',
                            fontSize: 18,
                            cursor: 'pointer',
                            padding: 4,
                            lineHeight: 1,
                            borderRadius: 999,
                        }}
                    >
                        ✕
                    </button>
                </div>

                {/* Corpo */}
                <div>
                    {!preview && (
                        <div
                            style={{
                                position: 'relative',
                                width: '100%',
                                borderRadius: 12,
                                overflow: 'hidden',
                                background: '#000',
                                aspectRatio: '3 / 4',
                                maxHeight: '65vh'
                            }}
                        >
                            <video
                                ref={videoRef}
                                className="video"
                                autoPlay
                                playsInline
                                muted
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover'
                                }}
                            />
                            {isStarting && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        inset: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        background: 'rgba(0,0,0,0.4)',
                                        fontSize: 13,
                                        color: '#e5e7eb'
                                    }}
                                >
                                    Abrindo câmera...
                                </div>
                            )}
                        </div>
                    )}

                    <canvas ref={canvasRef} className="hidden" style={{ display: 'none' }} />

                    {preview && (
                        <div
                            style={{
                                width: '100%',
                                borderRadius: 12,
                                overflow: 'hidden',
                                background: '#000',
                                maxHeight: '65vh'
                            }}
                        >
                            <img
                                src={preview}
                                alt="preview"
                                className="preview"
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'contain',
                                    display: 'block',
                                    background: '#000'
                                }}
                            />
                        </div>
                    )}

                    <p
                        className="badge"
                        style={{
                            marginTop: 8,
                            fontSize: 12,
                            color: '#9ca3af',
                            background: 'rgba(15,23,42,0.8)',
                            borderRadius: 999,
                            padding: '4px 10px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6
                        }}
                    >
                        {!preview && (
                            <span
                                style={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '999px',
                                    background: stream ? '#22c55e' : '#f97316',
                                    boxShadow: stream
                                        ? '0 0 0 4px rgba(34,197,94,0.3)'
                                        : '0 0 0 4px rgba(249,115,22,0.3)'
                                }}
                            />
                        )}
                        {status || 'Pronto para capturar'}
                    </p>
                </div>

                {/* Footer / botões */}
                <div
                    className="modal-footer"
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 8,
                        marginTop: 12
                    }}
                >
                    {!preview ? (
                        <>
                            <button
                                className="btn btn--ghost"
                                type="button"
                                onClick={toggleCamera}
                                disabled={isStarting || !navigator.mediaDevices}
                                style={{
                                    flex: 1,
                                    fontSize: 13,
                                    padding: '8px 10px',
                                    color: '#fff'
                                }}
                            >
                                🔄 Trocar câmera
                            </button>
                            <button
                                className="btn btn--primary"
                                type="button"
                                onClick={doCapture}
                                disabled={isStarting || !stream}
                                style={{
                                    flex: 1.2,
                                    fontSize: 13,
                                    padding: '8px 10px',
                                    color: '#fff'
                                }}
                            >
                                📸 Capturar
                            </button>
                        </>
                    ) : (
                        <>
                            <button
                                className="btn btn--ghost"
                                type="button"
                                onClick={handleRetake}
                                style={{
                                    flex: 1,
                                    fontSize: 13,
                                    padding: '8px 10px',
                                    color: '#fff'
                                }}
                            >
                                Tirar outra
                            </button>
                            <button
                                className="btn btn--success"
                                type="button"
                                onClick={usePhoto}
                                style={{
                                    flex: 1.2,
                                    fontSize: 13,
                                    padding: '8px 10px',
                                    color: '#fff'
                                }}
                            >
                                ✅ Enviar
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    )
}